import { Controller } from "@hotwired/stimulus"
import Uppy from "@uppy/core"
import DragDrop from "@uppy/drag-drop"
import Tus from "@uppy/tus"
import ProgressBar from "@uppy/progress-bar"
import StatusBar from "@uppy/status-bar"

// Birthday Hero intake only; the standalone reward-video flow is unchanged.
export default class extends Controller {
    static targets = ["dropzone", "progressMount", "statusMount", "error", "fileName", "uploadUrl", "message", "retry", "remove"]
    static values = { endpoint: String, completeUrl: String, token: String }

    connect() {
        this.connection = Symbol("hero-upload")
        this.uppy = new Uppy({ debug: false, autoProceed: true,
            restrictions: { maxNumberOfFiles: 1, maxFileSize: 1024 * 1024 * 1024, allowedFileTypes: ["video/*"] },
            meta: { hero_token: this.tokenValue }
        })
        this.uppy.use(DragDrop, { target: this.dropzoneTarget })
        this.uppy.use(Tus, {
            endpoint: this.endpointValue, chunkSize: 50 * 1024 * 1024,
            retryDelays: [0, 1000, 3000, 5000],
            // A previous party's browser fingerprint must never choose the
            // destination for this signed intake. In-page retries still work.
            storeFingerprintForResuming: false, removeFingerprintOnSuccess: true,
            urlStorage: {
                findUploadsByFingerprint: async () => [], findAllUploads: async () => [],
                addUpload: async () => null, removeUpload: async () => {}
            },
            onBeforeRequest: request => {
                // Never send our CSRF token to the third-party PATCH destination.
                if (new URL(request.getURL(), location.href).origin === location.origin) {
                    request.setHeader("X-CSRF-Token", this.csrfToken())
                }
            }
        })
        this.uppy.use(ProgressBar, { target: this.progressMountTarget, hideAfterFinish: false })
        this.uppy.use(StatusBar, { target: this.statusMountTarget, showProgressDetails: true,
            hidePauseResumeButton: true, hideCancelButton: true, hideAfterFinish: false,
            locale: { strings: { complete: "Upload complete" } }
        })
        this.uppy.on("file-added", file => {
            this.uppy.setFileMeta(file.id, { upload_key: crypto.randomUUID() })
            this.setState("uploading", "Uploading your video. Please keep this page open.")
            this.removeTarget.classList.remove("d-none")
            this.errorTarget.classList.add("d-none")
        })
        this.uppy.on("upload-success", (file, response) => {
            this.pendingVideo = { url: response.uploadURL, name: file.name }
            this.retrySave()
        })
        this.uppy.on("upload-error", () => this.uploadError("The upload stopped. Please remove it and choose the video again, or continue without it."))
        this.uppy.on("error", () => this.uploadError("The upload stopped. Please remove it and choose the video again, or continue without it."))
        if (this.uploadUrlTarget.value) {
            this.dropzoneTarget.classList.add("d-none")
            this.removeTarget.classList.remove("d-none")
            this.setState("ready", `Video saved: ${this.fileNameTarget.value}`)
        }
    }

    async retrySave() {
        if (!this.pendingVideo || this.saving) return
        const connection = this.connection
        const video = this.pendingVideo
        this.saving = true
        this.retryTarget.classList.add("d-none")
        this.errorTarget.classList.add("d-none")
        this.setState("saving", "Upload complete. Saving your video…")
        try {
            this.saveRequest = new AbortController()
            const timeout = setTimeout(() => this.saveRequest?.abort(), 35000)
            let response
            try {
                response = await fetch(this.completeUrlValue, {
                    method: "POST", credentials: "same-origin", signal: this.saveRequest.signal,
                    headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": this.csrfToken() },
                    body: JSON.stringify({ hero_token: this.tokenValue, upload_url: video.url })
                })
            } finally { clearTimeout(timeout) }
            if (!response.ok) throw new Error("Save not confirmed")
            const result = await response.json()
            if (result.ok !== true) throw new Error("Save not confirmed")
            if (this.connection !== connection || this.pendingVideo !== video) return
            this.fileNameTarget.value = result.file_name || video.name
            this.uploadUrlTarget.value = video.url
            this.dropzoneTarget.classList.add("d-none")
            this.setState("ready", `Video saved: ${this.fileNameTarget.value}. You can now submit your story.`)
        } catch (_) {
            if (this.connection !== connection || this.pendingVideo !== video) return
            this.uploadError("Your video upload finished, but saving hasn't been confirmed. Try saving again—there's no need to upload it again.")
            this.retryTarget.classList.remove("d-none")
        } finally {
            if (this.connection === connection && this.pendingVideo === video) this.saving = false
        }
    }

    removeVideo() {
        this.saveRequest?.abort()
        this.pendingVideo = null
        this.saving = false
        this.uppy.cancelAll()
        this.fileNameTarget.value = this.uploadUrlTarget.value = ""
        this.retryTarget.classList.add("d-none")
        this.removeTarget.classList.add("d-none")
        this.errorTarget.classList.add("d-none")
        this.dropzoneTarget.classList.remove("d-none")
        this.setState("idle", "No video selected. You can continue with your story and photo.")
    }

    uploadError(message) {
        this.errorTarget.textContent = message
        this.errorTarget.classList.remove("d-none")
        this.setState("error", "Your form details are still here.")
    }

    setState(state, message) {
        this.messageTarget.textContent = message
        this.dispatch("state", { detail: { state } })
    }

    csrfToken() { return document.querySelector('meta[name="csrf-token"]')?.content || "" }

    disconnect() {
        this.connection = null
        this.saveRequest?.abort()
        this.uppy?.destroy()
        this.uppy = null
    }
}
